import { UserID } from "../user/user.interface"
import { ITripRoomGuest, TripRoomGuestID } from "../trips/trip-room-guest.interface"
import {
  CruisePassengerAgeGroup,
  CruisePassengerGender,
} from "../cruises/cruise-passenger.interface"
import { IProfile } from "../user/profile.interface"
import { UserTitle } from "../user/user-address.interface"

export interface ITripBookingPassenger {
  userId?: UserID | null
  tripRoomGuestId: TripRoomGuestID
  profileId?: number
  profileOwnerId?: UserID

  ageGroup: CruisePassengerAgeGroup
  dob: string

  title: UserTitle
  gender?: CruisePassengerGender
  firstName: string
  lastName: string
  email?: string

  /** two-letter country code ex. "GB" */
  nationality: string
  address: string
  /** two-letter county/state code ex. "AZ" */
  county?: string
  postcode: string
  city: string
  /** two-letter country code ex. "GB" */
  country: string

  /** Required for cruises departing in the next 21 days, this is the masked passengers passport number. */
  passportNumber?: string
  /** Required for cruises departing in the next 21 days, this is the passport issue date. */
  passportIssueDate?: string
  /** Required for cruises departing in the next 21 days, this is the passport expiry date. */
  passportExpireDate?: string
  /** Country of passport (not required but recommended) */
  passportIssuingCountry?: string
  passportTokenId?: string

  /** Loyalty number of passenger for cruise line on booking. */
  loyaltyNumber?: string

  /** Emergency contact is required as Best Practice for some cruise lines e.g. Star Cruises and MSC */
  emergencyName?: string
  emergencyPhone?: string
  emergencyEmail?: string
}

/**
 * Determines if two passengers are the same based on their user IDs or trip room guest IDs.
 *
 * @param {ITripBookingPassenger} passenger1 - The first passenger object to compare.
 * @param {ITripBookingPassenger} passenger2 - The second passenger object to compare.
 * @return {boolean} - True if the passengers are the same, false otherwise.
 */
export const isSamePassenger = (
  passenger1: ITripBookingPassenger,
  passenger2: ITripBookingPassenger,
): boolean => {
  return (
    passenger1.userId === passenger2?.userId &&
    passenger1.tripRoomGuestId === passenger2?.tripRoomGuestId
  )
}

/**
 * Transforms a guest profile into a passenger object for a trip booking.
 *
 * @param {IProfile} guestProfile - The guest profile to transform.
 * @param {ITripRoomGuest} [guest] - The trip room guest associated with the profile (optional).
 * @return {ITripBookingPassenger} - The transformed passenger object.
 */
export const transformProfileToPassenger = (
  guestProfile: IProfile,
  guest?: ITripRoomGuest,
): ITripBookingPassenger => {
  return {
    userId: guest?.userId || (guestProfile.isPrimaryProfile ? guestProfile.ownerUserId : null),
    profileId: guest?.profileId || guestProfile.id,
    tripRoomGuestId: guest?.id || "",
    profileOwnerId: guestProfile?.ownerUserId || "",
    title: guestProfile.title || "",
    firstName: guestProfile.firstName,
    lastName: guestProfile.lastName,
    ageGroup: guestProfile.ageGroup || "adult",
    dob: guestProfile.dob || guest?.dob || "",
    nationality: guestProfile.nationality || "",
    address: guestProfile.address || "",
    country: guestProfile.country || "",
    county: guestProfile.county || "",
    postcode: guestProfile.postcode || "",
    city: guestProfile.city || "",
    passportNumber: guestProfile.passportNumber || "",
    passportIssueDate: guestProfile.passportIssueDate || "",
    passportExpireDate: guestProfile.passportExpireDate || "",
    passportIssuingCountry: guestProfile.passportIssuingCountry || "",
    passportTokenId: guestProfile.passportTokenId || "",
  }
}

/**
 * Transforms a guest object into a passenger object for a trip booking.
 *
 * @param {ITripRoomGuest} guest - The guest object to transform.
 * @return {ITripBookingPassenger} The transformed passenger object.
 */
export const transformGuestToPassenger = (guest: ITripRoomGuest): ITripBookingPassenger => {
  if (!guest) return {} as ITripBookingPassenger

  return {
    userId: guest.userId,
    tripRoomGuestId: guest.id,
    profileId: guest?.profileId,
    title: "",
    firstName: guest.firstName,
    lastName: guest.lastName,
    ageGroup: guest.ageGroup,
    dob: guest.dob || "",
    nationality: "",
    address: "",
    country: "",
    county: "",
    postcode: "",
    city: "",
    passportNumber: "",
    passportIssueDate: "",
    passportExpireDate: "",
    passportIssuingCountry: "",
  }
}
